import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import moment from "moment";
import Langs, { Localize } from "../../app/lang/langs";
import NewsReadCheckbox from "../../components/dashboard/News/NewsReadCheckbox";
import NewsAttachmentsView from "../../components/dashboard/News/NewsAttachmentsView";

/**
 * App-wide gate: as long as the collab has unread *mandatory* news
 * (`news.mandatory = 1`), a non-dismissable carousel of those items covers the
 * application. He can browse them freely and confirm them in any order; each
 * confirmed item leaves the carousel, and the gate disappears once none are
 * left. Non-mandatory news never blocks — it only shows in the bell and list.
 *
 * Rendered from HomePage, so it stays up across every authenticated route.
 */
export default function UnreadNewsGate() {

    const newsList = useSelector((state) => state.dashboard.news);
    const userToken = useSelector((state) => state.login.userToken);
    const isMobile = useSelector((state) => state.settings.isMobile);
    const mobile = isMobile === 2;

    const [index, setIndex] = useState(0);

    // Only mandatory news blocks. Oldest first, so the collab works through the
    // backlog chronologically. `mandatory` arrives as a tinyint (0/1).
    const unread = useMemo(
        () => (newsList || [])
            .filter((news) => news && news.id && !news.seen && Number(news.mandatory) === 1)
            .sort((a, b) => (a.created_at || 0) - (b.created_at || 0)),
        [newsList]
    );

    // Confirming an item removes it from the list: keep the index in range so
    // the carousel lands on the next pending item instead of going blank.
    useEffect(() => {
        setIndex((previous) => (previous >= unread.length ? Math.max(unread.length - 1, 0) : previous));
    }, [unread.length]);

    const total = unread.length;
    const current = unread[Math.min(index, Math.max(total - 1, 0))];

    if (!userToken || !total || !current) return null;

    const goTo = (nextIndex) => {
        if (total < 2) return;
        setIndex(((nextIndex % total) + total) % total);
    };

    return (
        <Dialog
            open
            // No onClose and no close button: the only way out is confirming
            // every item. Esc, the backdrop and the browser UI cannot dismiss it.
            disableEscapeKeyDown
            fullScreen={mobile}
            maxWidth="sm"
            fullWidth
            aria-labelledby="unread-news-gate-title"
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: "rgba(0, 40, 128, 0.45)",
                        backdropFilter: "blur(6px)",
                        WebkitBackdropFilter: "blur(6px)",
                    },
                },
                paper: {
                    sx: {
                        borderRadius: mobile ? 0 : "20px",
                        overflow: "hidden",
                        border: mobile ? "none" : "1px solid rgba(255, 144, 21, 0.16)",
                        boxShadow: "0 24px 80px rgba(0, 40, 128, 0.24)",
                        display: "flex",
                        flexDirection: "column",
                    },
                },
            }}
        >
            {/* Header — what this is and how far through the backlog he is */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: mobile ? 2 : 3,
                    py: mobile ? 1.5 : 2,
                    background: "linear-gradient(135deg, rgba(255, 144, 21, 0.10), rgba(0, 40, 128, 0.06))",
                    borderBottom: "1px solid rgba(255, 144, 21, 0.14)",
                }}
            >
                <CampaignOutlinedIcon sx={{ color: "#FF9015" }} />
                <Typography
                    id="unread-news-gate-title"
                    sx={{ fontWeight: 600, fontSize: "1.05rem", letterSpacing: "-0.02em", color: "#002880", flex: 1 }}
                >
                    <Langs str="NEWS_GATE_TITLE" />
                </Typography>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#6b6560" }}>
                    {`${Math.min(index, total - 1) + 1} / ${total}`}
                </Typography>
            </Box>

            {/* Current item — the message scrolls, the confirmation stays put */}
            <Box
                key={current.id}
                sx={{
                    px: mobile ? 2 : 3,
                    py: mobile ? 2 : 2.5,
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    animation: "unreadNewsGateFade 260ms cubic-bezier(0.4, 0, 0.2, 1) both",
                    "@keyframes unreadNewsGateFade": {
                        from: { opacity: 0, transform: "translateX(12px)" },
                        to: { opacity: 1, transform: "translateX(0)" },
                    },
                }}
            >
                <Typography sx={{ fontWeight: 600, fontSize: mobile ? "1rem" : "1.1rem", color: "#2d2a26" }}>
                    {current.title}
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "#8a8378" }}>
                    {[current.created_by, current.created_at && moment(current.created_at).format("DD/MM/YYYY HH:mm")]
                        .filter(Boolean)
                        .join(" • ")}
                </Typography>
                <Box
                    sx={{
                        mt: 0.5,
                        overflowY: "auto",
                        maxHeight: mobile ? "unset" : 280,
                        flex: mobile ? 1 : "unset",
                        fontSize: "0.9rem",
                        lineHeight: 1.6,
                        color: "#3a3632",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                    }}
                >
                    {current.message}
                    {/* Inside the scrolling area, not below it: a picture or a document is
                        part of the announcement the collab is being asked to confirm. */}
                    <Box sx={{ mt: 1.5 }}>
                        <NewsAttachmentsView attachments={current.attachments} />
                    </Box>
                </Box>
            </Box>

            <Box sx={{ px: mobile ? 2 : 3, pb: 1 }}>
                <NewsReadCheckbox news={current} />
            </Box>

            {/* Carousel controls + why the application is locked */}
            <Box
                sx={{
                    px: mobile ? 2 : 3,
                    py: 1.5,
                    borderTop: "1px solid rgba(255, 144, 21, 0.12)",
                    background: "rgba(255, 250, 242, 0.7)",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                    <IconButton
                        onClick={() => goTo(index - 1)}
                        disabled={total < 2}
                        aria-label={Localize("NEWS_GATE_PREVIOUS")}
                        sx={{ color: "#002880" }}
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", justifyContent: "center" }}>
                        {unread.map((news, dotIndex) => (
                            <Box
                                key={news.id}
                                component="button"
                                type="button"
                                onClick={() => setIndex(dotIndex)}
                                aria-label={`${Localize("NEWS_GATE_TITLE")} ${dotIndex + 1}`}
                                aria-current={dotIndex === index}
                                sx={{
                                    width: dotIndex === index ? 22 : 8,
                                    height: 8,
                                    p: 0,
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    backgroundColor: dotIndex === index ? "#FF9015" : "rgba(0, 40, 128, 0.22)",
                                    transition: "all 220ms cubic-bezier(0.4, 0, 0.2, 1)",
                                }}
                            />
                        ))}
                    </Box>
                    <IconButton
                        onClick={() => goTo(index + 1)}
                        disabled={total < 2}
                        aria-label={Localize("NEWS_GATE_NEXT")}
                        sx={{ color: "#002880" }}
                    >
                        <ChevronRightIcon />
                    </IconButton>
                </Box>
                <Typography sx={{ mt: 0.5, textAlign: "center", fontSize: "0.75rem", color: "#b06000" }}>
                    <Langs
                        str={total === 1 ? "NEWS_GATE_REMAINING_ONE" : "NEWS_GATE_REMAINING_MANY"}
                        params={{ count: total }}
                    />
                </Typography>
            </Box>
        </Dialog>
    );
}
